import React, { useEffect, useRef, useState } from 'react';
import api from '../api';

const today = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const TPTYPE_LABEL = { T: '학과', P: '실기' };

// 평가결과 입력(관리자 전용, 평가결과조회 페이지의 [입력] 버튼으로 진입): 일자/반/과목/구분을 고르고 [평가자료생성]으로 반 학생 명단을 불러온 뒤 점수를 입력해 저장한다.
// mobile: 스마트폰에서는 모든 행에서 같은 값인 열(학년/반/과목/구분/평가일자)을 숨긴다.
export default function ExamScores({ mobile = false, onNavigate }) {
  const [exmdate, setExmdate] = useState(today());
  const [classtype, setClasstype] = useState('M');
  const [grade, setGrade] = useState(1);
  const [classOptions, setClassOptions] = useState([]);
  const [classNo, setClassNo] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [amtcode, setAmtcode] = useState('');
  const [tptype, setTptype] = useState('T');

  const [rows, setRows] = useState([]); // { studid, studno, name, grade, class_no, score(문자열), remark }
  const [snapshot, setSnapshot] = useState(null); // 평가자료생성 당시의 조회조건 (표 표시/저장에 사용)
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const scoreRefs = useRef([]);

  useEffect(() => {
    api.get('/exam-subjects').then((res) => {
      setSubjects(res.data);
      if (res.data.length) setAmtcode((prev) => prev || res.data[0].amtcode);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.get('/classes', { params: { grade, classtype: classtype === 'M' ? 'M' : 'R' } }).then((res) => {
      if (cancelled) return;
      setClassOptions(res.data);
      setClassNo(res.data.length ? String(res.data[0]) : '');
    });
    return () => {
      cancelled = true;
    };
  }, [grade, classtype]);

  // 조회조건이 바뀌면 이전에 불러온 명단은 더 이상 맞지 않으므로 비운다
  useEffect(() => {
    setRows([]);
    setSnapshot(null);
    setMessage('');
  }, [exmdate, classtype, grade, classNo, amtcode, tptype]);

  const subjectName = (code) => subjects.find((s) => s.amtcode === code)?.subject || code;

  const handleGenerate = async () => {
    if (!exmdate || !classNo || !amtcode) {
      setMessage('일자, 반, 과목을 선택하세요.');
      return;
    }
    setLoading(true);
    setMessage('');
    try {
      const res = await api.get('/exam-scores', {
        params: { exmdate, classtype, grade, class_no: Number(classNo), amtcode, tptype },
      });
      setSnapshot({ exmdate, classtype, grade, classNo: Number(classNo), amtcode, tptype });
      setRows(res.data.map((r) => ({ ...r, score: r.score == null ? '' : String(r.score) })));
      scoreRefs.current = [];
      if (res.data.length === 0) setMessage('해당 반에 재학생이 없습니다.');
    } catch (err) {
      setMessage(err.response?.data?.detail || '평가자료를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const handleSave = async () => {
    if (!snapshot || rows.length === 0) {
      setMessage('먼저 평가자료생성을 눌러 학생 명단을 불러오세요.');
      return;
    }
    for (const r of rows) {
      if (r.score === '') continue;
      const n = Number(r.score);
      if (!Number.isInteger(n) || n < 0 || n > 100) {
        setMessage(`${r.name}: 점수는 0~100 사이의 정수로 입력하세요.`);
        return;
      }
    }
    setSaving(true);
    setMessage('');
    try {
      const res = await api.post('/exam-scores', {
        exmdate: snapshot.exmdate,
        amtcode: snapshot.amtcode,
        tptype: snapshot.tptype,
        items: rows.map((r) => ({
          studid: r.studid,
          score: r.score === '' ? null : Number(r.score),
          remark: r.remark || '',
        })),
      });
      setMessage(`${res.data.message} (저장 ${res.data.saved}건${res.data.deleted ? `, 삭제 ${res.data.deleted}건` : ''})`);
    } catch (err) {
      setMessage(err.response?.data?.detail || '저장하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  // 삭제: 현재 선택한 일자/반구분/학년/반/과목/실기·학과에 해당하는 평가자료를 모두 지운다
  const handleDelete = async () => {
    if (!exmdate || !classNo || !amtcode) {
      setMessage('일자, 반, 과목을 선택하세요.');
      return;
    }
    const label = `${exmdate} / ${classtype === 'M' ? '면장반' : '정규반'} ${grade}학년 ${classNo}반 / ${subjectName(amtcode)} / ${TPTYPE_LABEL[tptype]}`;
    if (!window.confirm(`${label}

위 조건의 평가자료를 모두 삭제합니다. 삭제하시겠습니까?`)) return;
    setDeleting(true);
    setMessage('');
    try {
      const res = await api.delete('/exam-scores', {
        params: { exmdate, classtype, grade, class_no: Number(classNo), amtcode, tptype },
      });
      // 삭제 결과를 반영해 명단을 다시 불러온다 (명단이 열려 있었던 경우)
      if (snapshot) await handleGenerate();
      setMessage(res.data.deleted ? `삭제되었습니다. (${res.data.deleted}건)` : res.data.message);
    } catch (err) {
      setMessage(err.response?.data?.detail || '삭제하지 못했습니다.');
    } finally {
      setDeleting(false);
    }
  };

  // 점수 칸에서 Enter를 누르면 다음 학생의 점수 칸으로 이동
  const handleScoreKey = (e, idx) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const next = scoreRefs.current[idx + 1];
    if (next) next.focus();
  };

  const hideCls = mobile ? ' exam-col-hide' : '';

  return (
    <div className={`page exam-scores-page${mobile ? ' exam-scores-mobile' : ''}`}>
      <div className="page-header">
        <h2>항공정비사과정 평가결과입력</h2>
        {onNavigate && (
          <button type="button" className="btn-ghost" onClick={() => onNavigate('exam-results')}>
            평가결과조회
          </button>
        )}
      </div>

      <div className="card">
        <div className="form-row exam-filter-row">
          <div className="form-field">
            <label>일자</label>
            <input type="date" value={exmdate} onChange={(e) => setExmdate(e.target.value)} />
          </div>
          <div className="form-field">
            <label>반구분</label>
            <select value={classtype} onChange={(e) => setClasstype(e.target.value)}>
              <option value="R">정규반</option>
              <option value="M">면장반</option>
            </select>
          </div>
          <div className="form-field">
            <label>학년</label>
            <select value={grade} onChange={(e) => setGrade(Number(e.target.value))}>
              <option value={1}>1학년</option>
              <option value={2}>2학년</option>
              <option value={3}>3학년</option>
            </select>
          </div>
          <div className="form-field">
            <label>반</label>
            <select value={classNo} onChange={(e) => setClassNo(e.target.value)}>
              {classOptions.length === 0 && <option value="">-</option>}
              {classOptions.map((c) => (
                <option key={c} value={c}>{c}반</option>
              ))}
            </select>
          </div>
          <div className="form-field exam-field-subject">
            <label>과목</label>
            <select value={amtcode} onChange={(e) => setAmtcode(e.target.value)}>
              {subjects.map((s) => (
                <option key={s.amtcode} value={s.amtcode}>{s.subject}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label>실기/학과</label>
            <select value={tptype} onChange={(e) => setTptype(e.target.value)}>
              <option value="T">학과</option>
              <option value="P">실기</option>
            </select>
          </div>
          <div className="form-field exam-field-actions">
            <label>&nbsp;</label>
            <div className="exam-actions">
              <button type="button" className="btn-ghost exam-btn" onClick={handleGenerate} disabled={loading}>
                {loading ? '불러오는 중...' : '평가자료생성'}
              </button>
              <button
                type="button"
                className="btn-primary exam-btn"
                onClick={handleSave}
                disabled={saving || rows.length === 0}
              >
                {saving ? '저장 중...' : '저장'}
              </button>
              <button
                type="button"
                className="btn-ghost exam-btn exam-btn-delete"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? '삭제 중...' : '삭제'}
              </button>
            </div>
          </div>
        </div>

        {message && <div className="form-message">{message}</div>}

        <div className="table-scroll">
          <table className="data-table exam-scores-table">
            <thead>
              <tr>
                <th className="exam-col-no">번호</th>
                <th className={`exam-col-sm${hideCls}`}>학년</th>
                <th className={`exam-col-sm${hideCls}`}>반</th>
                <th className="exam-col-name">이름</th>
                <th className={hideCls.trim()}>과목</th>
                <th className={`exam-col-tp${hideCls}`}>실기/학과</th>
                <th className={`exam-col-date${hideCls}`}>평가일자</th>
                <th className="exam-col-score">점수</th>
                <th>비고</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.studid}>
                  <td>{r.studno ?? i + 1}</td>
                  <td className={hideCls.trim()}>{r.grade}</td>
                  <td className={hideCls.trim()}>{r.class_no}</td>
                  <td>{r.name}</td>
                  <td className={hideCls.trim()}>{subjectName(snapshot.amtcode)}</td>
                  <td className={hideCls.trim()}>{TPTYPE_LABEL[snapshot.tptype]}</td>
                  <td className={hideCls.trim()}>{snapshot.exmdate}</td>
                  <td>
                    <input
                      ref={(el) => {
                        scoreRefs.current[i] = el;
                      }}
                      className="exam-score-input"
                      type="number"
                      inputMode="numeric"
                      min="0"
                      max="100"
                      value={r.score}
                      onChange={(e) => updateRow(i, { score: e.target.value })}
                      onKeyDown={(e) => handleScoreKey(e, i)}
                    />
                  </td>
                  <td>
                    <input
                      className="exam-remark-input"
                      type="text"
                      maxLength={255}
                      value={r.remark}
                      onChange={(e) => updateRow(i, { remark: e.target.value })}
                    />
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="empty-row">
                    조회조건을 선택하고 [평가자료생성]을 누르면 해당 반 학생이 나열됩니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
